import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaSeedling } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateGenericPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const KVPCalculator = () => {
  const [principal, setPrincipal] = useState(100000);
  const [interestRate, setInterestRate] = useState(7.5); // Current KVP rate
  const [result, setResult] = useState(null);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculate();
  }, [principal, interestRate]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculate = () => {
    // KVP: Doubles money at a fixed interest rate
    // Time to double = 72 / interest rate (Rule of 72)
    // Or exact: ln(2) / ln(1 + r)
    
    const rate = interestRate / 100;
    
    // Calculate months to double using compound interest formula
    // 2P = P(1 + r/4)^(4t) for quarterly compounding
    // 2 = (1 + r/4)^(4t)
    // t = ln(2) / (4 * ln(1 + r/4))
    
    const quarterlyRate = rate / 4;
    const quartersToDouble = Math.log(2) / Math.log(1 + quarterlyRate);
    const monthsToDouble = Math.ceil(quartersToDouble * 3);
    const yearsToDouble = (monthsToDouble / 12).toFixed(1);
    
    const maturityAmount = principal * 2;
    const totalInterest = principal;
    
    // Generate yearly growth data
    const yearlyData = [];
    let balance = principal;
    const totalYears = Math.ceil(monthsToDouble / 12);
    
    for (let year = 1; year <= totalYears; year++) {
      const quarters = Math.min(4, (monthsToDouble / 3) - ((year - 1) * 4));
      for (let q = 0; q < quarters; q++) {
        balance = balance * (1 + quarterlyRate);
      }
      
      yearlyData.push({
        year,
        balance: Math.min(Math.round(balance), maturityAmount),
      });
    }
    
    setResult({
      maturityAmount,
      totalInterest,
      monthsToDouble,
      yearsToDouble,
      yearlyData,
    });
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    generateGenericPDF('Kisan Vikas Patra Report', {
      'Investment Amount': formatCurrency(principal),
      'Interest Rate': `${interestRate}% p.a.`,
      'Time to Double': `${result.monthsToDouble} months (${result.yearsToDouble} years)`,
      'Maturity Amount': formatCurrency(result.maturityAmount),
      'Total Interest': formatCurrency(result.totalInterest),
    });
  };

  const handleReset = () => {
    setPrincipal(100000);
    setInterestRate(7.5);
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="KVP Calculator"
        description="Free Kisan Vikas Patra Calculator - Calculate KVP maturity. See how investment doubles with current rates."
        keywords="KVP calculator, Kisan Vikas Patra calculator, KVP maturity calculator, KVP doubling time, post office KVP"
        canonicalPath="/kvp"
      />
      <div className="calculator-header">
        <h1><FaSeedling style={{ color: '#22c55e' }} /> Kisan Vikas Patra (KVP)</h1>
        <p>Calculate returns for KVP - Doubles your money at guaranteed rate</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Investment Amount (₹)</label>
            <div className="input-with-icon">
              <FaRupeeSign className="icon" />
              <input
                type="number"
                value={principal === '' ? '' : principal}
                onChange={(e) => setPrincipal(e.target.value === '' ? '' : Number(e.target.value))}
                min="1000"
              />
            </div>
            <small style={{ color: 'var(--text-secondary)' }}>Min: ₹1,000, In multiples of ₹100</small>
            <div className="slider-container">
              <input
                type="range"
                min="1000"
                max="1000000"
                step="1000"
                value={principal || 0}
                onChange={(e) => setPrincipal(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="input-group">
            <label>Interest Rate (% p.a.)</label>
            <input
              type="number"
              value={interestRate === '' ? '' : interestRate}
              onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              min="1"
              max="15"
              step="0.1"
            />
            <small style={{ color: 'var(--text-secondary)' }}>Current rate: 7.5% (Q1 FY 2024-25)</small>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: '8px' }} /> Download PDF
            </button>
          </div>
        </div>

        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)' }}>
                <h3>Your Money Doubles To</h3>
                <div className="amount">{formatCurrency(result.maturityAmount)}</div>
                <small>in {result.monthsToDouble} months ({result.yearsToDouble} years)</small>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Principal Invested</span>
                  <span className="value">{formatCurrency(principal)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Earned</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Time to Double</span>
                  <span className="value">{result.monthsToDouble} months</span>
                </div>
                <div className="result-item">
                  <span className="label">Interest Rate</span>
                  <span className="value">{interestRate}% p.a.</span>
                </div>
                <div className="result-item" style={{ background: '#fef3c7' }}>
                  <span className="label">Growth</span>
                  <span className="value" style={{ color: '#d97706', fontWeight: 'bold' }}>2x (100% Return)</span>
                </div>
              </div>

              {/* Year-wise Growth */}
              <div className="breakup-section">
                <h4>Year-wise Growth</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Year</th>
                      <th>Balance</th>
                      <th>Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>0</td>
                      <td>{formatCurrency(principal)}</td>
                      <td>-</td>
                    </tr>
                    {result.yearlyData.map((row, index) => (
                      <tr key={index}>
                        <td>{row.year}</td>
                        <td style={{ fontWeight: 'bold' }}>{formatCurrency(row.balance)}</td>
                        <td style={{ color: '#10b981' }}>
                          {((row.balance / principal - 1) * 100).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 KVP Key Features</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <ul>
              <li>Guaranteed to double your investment</li>
              <li>Minimum: ₹1,000, No maximum limit</li>
              <li>Compounded quarterly</li>
              <li>Lock-in period: 2.5 years</li>
              <li>Premature encashment after 2.5 years</li>
            </ul>
          </div>
          <div>
            <ul>
              <li>Available at Post Offices & banks</li>
              <li>Can be pledged as loan collateral</li>
              <li>Transferable between persons</li>
              <li>No tax deduction at source</li>
              <li>Interest is taxable as per slab</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KVPCalculator;
